/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';
import {
  getColor,
  getTextStyle,
  pd,
  Orientation,
} from '@airasia-phoenix/react-native/utils/utils';
import ChipGroupView from '@airasia-phoenix/react-native/components/ChipGroupView';
import DividerView from '@airasia-phoenix/react-native/components/DividerView';
import { DividerType } from '@airasia-phoenix/react-native/components/DividerView/types';
import {
  CabinClassType,
  ChipViewWithValue,
  PassengerClassSelectionProps,
  SelectedValues,
} from './types';
import PassengerItem from './PassengerItem';
import Constants from './constants';
import { StyleSheet } from 'react-native-unistyles';
import TextView from '@airasia-phoenix/react-native/components/TextView';

const parseStepper = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const PassengerClassSelectionView = memo(
  (props: PassengerClassSelectionProps) => {
    const { hideCabinClass } = props;

    const isControlled = !!props.onChange;

    // Internal state only used when uncontrolled
    const [adultCount, setAdultCount] = useState(props.adultCount);
    const [childCount, setChildCount] = useState(props.childCount);
    const [infantCount, setInfantCount] = useState(props.infantCount);
    const [chipGroupViewProps, setChipGroupViewProps] = useState<
      ChipViewWithValue[]
    >(props.chipGroupViewProps.chipViewProps);

    const selectedValues = useRef<SelectedValues>({
      cabinClass: props.cabinClass,
      adult: props.adultCount,
      child: props.childCount,
      infant: props.infantCount,
    });

    // --- Sync external props -> internal state (ONLY when controlled) ---
    useEffect(() => {
      if (!isControlled) return;

      setAdultCount(props.adultCount);
      setChildCount(props.childCount);
      setInfantCount(props.infantCount);

      selectedValues.current = {
        cabinClass: props.cabinClass,
        adult: props.adultCount,
        child: props.childCount,
        infant: props.infantCount,
      };

      setChipGroupViewProps((prev) =>
        prev.map((item) => ({
          ...item,
          isSelected: item.value === props.cabinClass,
        })),
      );
    }, [
      isControlled,
      props.adultCount,
      props.childCount,
      props.infantCount,
      props.cabinClass,
    ]);

    // --- Derive max values (no state needed) ---
    const maxAdult = useMemo(() => {
      return props.maxTotal - (childCount + infantCount);
    }, [props.maxTotal, childCount, infantCount]);

    const minAdult = useMemo(() => {
      return Math.max(Constants.DEFAULT_ADULT_COUNT, infantCount);
    }, [infantCount]);

    const maxChild = useMemo(() => {
      return props.maxTotal - (adultCount + infantCount);
    }, [props.maxTotal, adultCount, infantCount]);

    const maxInfant = useMemo(() => {
      const byTotal = props.maxTotal - (adultCount + childCount);
      return Math.min(byTotal, adultCount); // infant <= adult
    }, [props.maxTotal, adultCount, childCount]);

    const emitChange = useCallback(() => {
      props.onChange?.(selectedValues.current);
    }, [props.onChange]);

    const handleOnChangeAdult = useCallback(
      (v: string) => {
        const n = parseStepper(v);
        if (n === null) return;

        selectedValues.current = { ...selectedValues.current, adult: n };

        if (isControlled) {
          emitChange();
          return;
        }

        setAdultCount(n);

        // clamp infant if needed (infant <= adult AND total cap)
        setInfantCount((prev) =>
          Math.min(prev, Math.min(props.maxTotal - (n + childCount), n)),
        );
      },
      [isControlled, emitChange, props.maxTotal, childCount],
    );

    const handleOnChangeChild = useCallback(
      (v: string) => {
        const n = parseStepper(v);
        if (n === null) return;

        selectedValues.current = { ...selectedValues.current, child: n };

        if (isControlled) {
          emitChange();
          return;
        }

        setChildCount(n);

        // clamp infant if needed
        setInfantCount((prev) =>
          Math.min(
            prev,
            Math.min(props.maxTotal - (adultCount + n), adultCount),
          ),
        );
      },
      [isControlled, emitChange, props.maxTotal, adultCount],
    );

    const handleOnChangeInfant = useCallback(
      (v: string) => {
        const n = parseStepper(v);
        if (n === null) return;

        selectedValues.current = { ...selectedValues.current, infant: n };

        if (isControlled) {
          emitChange();
          return;
        }

        setInfantCount(n);
      },
      [isControlled, emitChange],
    );

    const handleOnChangeClass = useCallback(
      (newProps: ChipViewWithValue[]) => {
        const selected =
          newProps.find((x) => x.isSelected)?.value ??
          Constants.DEFAULT_PASSENGER_CLASS;

        selectedValues.current = {
          ...selectedValues.current,
          cabinClass: selected,
        };

        // ensure default selected if none selected
        const normalized =
          selected === Constants.DEFAULT_PASSENGER_CLASS
            ? newProps.map((item) =>
                item.value === Constants.DEFAULT_PASSENGER_CLASS
                  ? { ...item, isSelected: true }
                  : item,
              )
            : newProps;

        if (isControlled) {
          props.onChange?.(selectedValues.current);
          // keep UI in sync for "default fallback" case
          if (selected === Constants.DEFAULT_PASSENGER_CLASS)
            setChipGroupViewProps(normalized);
        } else {
          setChipGroupViewProps(normalized);
        }
      },
      [isControlled, props.onChange],
    );

    return (
      <View
        style={[
          styles.container,
          {
            backgroundColor: getColor(pd.sys.colour.container_none),
          },
        ]}
      >
        <View style={styles.paxContainer}>
          <View style={styles.paxItemContainer}>
            <PassengerItem
              {...props.adultPassengerItemProps}
              inputStepperProps={{
                initialValue: adultCount,
                minValue: minAdult,
                maxValue: maxAdult,
                onChange: handleOnChangeAdult,
                disableTextInputEdit: true,
              }}
            />
          </View>

          <View style={styles.paxItemContainer}>
            <PassengerItem
              {...props.childPassengerItemProps}
              inputStepperProps={{
                initialValue: childCount,
                minValue: Constants.DEFAULT_CHILD_COUNT,
                maxValue: maxChild,
                onChange: handleOnChangeChild,
                disableTextInputEdit: true,
              }}
            />
          </View>

          <View style={styles.paxItemContainer}>
            <PassengerItem
              {...props.infantPassengerItemProps}
              inputStepperProps={{
                initialValue: infantCount,
                minValue: Constants.DEFAULT_CHILD_COUNT,
                maxValue: maxInfant,
                onChange: handleOnChangeInfant,
                disableTextInputEdit: true,
              }}
            />
          </View>

          {!hideCabinClass && (
            <DividerView
              type={DividerType.Solid}
              height={1}
              color={getColor(pd.sys.colour.outline_low)}
              width={'100%'}
            />
          )}
        </View>

        {!hideCabinClass && (
          <View style={styles.cabinClassContainer}>
            <View style={styles.cabinClassText}>
              <TextView
                text={props.cabinClassText}
                style={[
                  getTextStyle(
                    pd.sys.typescale.body_text_medium,
                    pd.sys.colour.content_primary,
                  ),
                  ,
                ]}
              />
            </View>

            <View style={styles.chipGroupContainer}>
              <ChipGroupView
                chipViewProps={chipGroupViewProps}
                onChange={handleOnChangeClass}
                orientation={
                  new Orientation.Grid(
                    2,
                    2,
                    pd.ref.space.spacing30.px,
                    pd.ref.space.spacing40.px,
                  )
                }
              />
            </View>
          </View>
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
  },
  paxItemContainer: { marginBottom: 16 },
  paxContainer: { width: '100%', paddingHorizontal: 24 },
  cabinClassContainer: { marginTop: 16, width: '100%', height: '100%' },
  chipGroupContainer: { paddingHorizontal: 24 },
  cabinClassText: {
    marginBottom: 16,
    paddingHorizontal: 24,
  },
});

export default PassengerClassSelectionView;
